import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { fullscreenBus } from './lib/fullscreenBus';
import { MapPage } from './pages/MapPage';
import { RidePage } from './pages/RidePage';
import { BatteryPage } from './pages/BatteryPage';
import { ProfilePage } from './pages/ProfilePage';
import { OfflinePage } from './pages/OfflinePage';
import { SOSPage } from './pages/SOSPage';
// Wave 14bv: NAVI-Hub ersetzt den direkten Trails-Tab als Einstieg für alles
// Routen-bezogene (Finger-Zeichnen, Custom-Tours).
import { NaviPage } from './pages/NaviPage';
import { CreateCustomPage } from './pages/CreateCustomPage';
import { RouterPage } from './pages/RouterPage';
import { CustomTrailsPage } from './pages/CustomTrailsPage';
import { CorridorDownloadsPage } from './pages/CorridorDownloadsPage';
import { CustomTrailDetailPage } from './pages/CustomTrailDetailPage';
import { LegalPage } from './pages/LegalPage';
import { useOnline } from './lib/useOnline';
import { DebugConsole } from './components/DebugConsole';
import { OutOfRegionBanner } from './components/OutOfRegionBanner';
import { useBoolPreference } from './lib/usePreference';
import { useWakeLock } from './lib/useWakeLock';
import { PREF_SCREEN_AWAKE } from './pages/ProfilePage';
import {
  CHROME_BG_ALPHA_PRIMARY,
  CHROME_BG_ALPHA_SECONDARY,
  CHROME_BLUR_PRIMARY,
  CHROME_BLUR_SECONDARY,
  CHROME_BORDER_ALPHA_PRIMARY,
  CHROME_BORDER_ALPHA_SECONDARY,
  ICON_OPACITY,
  ICON_OPACITY_SECONDARY,
  ICON_OPACITY_TERTIARY,
  ICON_SCALE,
  ICON_SCALE_SECONDARY,
  ICON_SCALE_TERTIARY,
  useIconSize,
  useAdminToken,
  useColorAssistMode,
} from './lib/useUiSettings';
import { flushCruxQueue } from './lib/admin';
import { flushElevationQueue } from './lib/elevationQueue';
import { flushAdminPoiQueue } from './lib/adminPois';
import { useGpsToRecorderBridge, useRecorderState } from './lib/useRideRecorder';
import { positionBus } from './lib/positionBus';
// Wave 14ca-fix64: Rain-Mode-Overlay app-weit mounten — schluckt Touches
// global wenn aktiv, unabhaengig davon auf welcher Route der User gerade ist.
import { RainModeOverlay } from './components/RainModeOverlay';
// Wave 14ca-fix64: Pocket-Mode-Overlay (OLED-Schwarz fuer Energie-Sparen).
// Gleicher Pattern wie Rain-Mode, anderer visueller Stil (pure black statt
// transparent + cyan border).
import { PocketModeOverlay } from './components/PocketModeOverlay';
import { GlobalRainModeGesture } from './components/GlobalRainModeGesture';
// Wave 14ca-fix30: cross-device-sync. Pull beim App-Start + bei jedem
// online-Wechsel; Push wird intern von cloudSync + customTrails getriggert.
import { installPrefSyncListener, pullPrefs } from './lib/cloudSync';
import { pullCustomTrails } from './lib/customTrails';
import { installRideSyncBackground, pullRides } from './lib/rideSync';

export function App() {
  const location = useLocation();

  // Screen Wake Lock — Bug #2. Präferenz wird in ProfilePage getoggled,
  // hier am Root gelesen damit der Lock app-weit gilt (nicht nur auf
  // MapPage). Der Hook kümmert sich selbst um das Re-acquire nach
  // visibilitychange → visible (iOS released das Sentinel beim Backgrounden).
  //
  // Wave 14ca-fix64: Waehrend Recording IMMER aktiv halten — auch wenn der
  // User screenAwake in Profile abgeschaltet hat. Hintergrund: User-Bug
  // "App hoert auf km/hm zu zaehlen wenn Display aus ist". Ursache ist
  // dass iOS Safari JS pausiert sobald die PWA in den Hintergrund geht
  // (Display aus oder Tab-Switch). Wake-Lock waehrend Recording verhindert
  // den Auto-Lock und damit den haeufigsten Trigger fuer die Hintergrund-
  // Pause. Kein 100%-Fix (User kann manuell sperren / iOS kann trotzdem
  // throttlen), aber der wichtigste Hebel der uns als PWA bleibt — bis
  // wir ein natives App-Wrap haben kann das Browser-API nicht mehr.
  const [screenAwake] = useBoolPreference(PREF_SCREEN_AWAKE, true);
  const recorderState = useRecorderState();
  const isRecording = recorderState.kind === 'recording';
  useWakeLock(screenAwake || isRecording);

  // Wave 14ca-fix64: Color-Assist-Mode app-weit syncen. Der Hook setzt das
  // Modul-Flag in shared/types.ts, damit alle DIFFICULTY_COLOR-Lookups
  // (Map-Linien, Swatches, Trail-Edit-Sheet) sofort in der richtigen
  // Palette rendern. App ist dauerhaft gemountet → der Sync bleibt aktiv.
  useColorAssistMode();

  // Wave 14ca-fix41: GPS → rideRecorder Bridge. Hook mountet einmal beim
  // App-Start, subscribed positionBus, und appendet jeden GPS-Fix wenn
  // gerade eine Free-Ride aufgezeichnet wird. Recorder selbst checkt den
  // 'recording'-State, daher kein Conditional Mount noetig.
  useGpsToRecorderBridge();

  // Wave 14ca-fix64: Hintergrund-Recovery. Wenn der Tab nach einer
  // visibilityState-'hidden'-Phase wieder visible wird waehrend Recording
  // aktiv ist, versuchen wir EINEN frischen getCurrentPosition zu holen.
  // Das fuettert positionBus → rideRecorder.appendPoint, womit zumindest
  // die Distanz/Hoehe zwischen letztem Punkt vor Background und erster
  // Position nach Resume in die Stats einfliesst. Wir verlieren weiter
  // die Geometrie der Strecke dazwischen (kein Workaround, iOS-Limit),
  // aber km + hm Endsumme stimmt damit halbwegs.
  useEffect(() => {
    if (!isRecording) return;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!('geolocation' in navigator)) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          // positionBus.set triggert die useGpsToRecorderBridge → appendPoint.
          positionBus.set(pos.coords);
        },
        () => {
          /* ignore — der laufende watchPosition uebernimmt sobald er weiter
             feuert; ein einmaliger Fail ist kein Problem. */
        },
        { enableHighAccuracy: true, timeout: 10_000, maximumAge: 5_000 },
      );
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isRecording]);

  // Glove-Mode: Skaliere alle Map-Overlays + MapLibre-Controls per CSS-Var.
  // Setzen auf :root damit sowohl unsere React-Overlays als auch MapLibre-
  // internes DOM (außerhalb unseres React-Trees) davon profitieren.
  const [iconSize] = useIconSize();
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--icon-scale', String(ICON_SCALE[iconSize]));
    root.style.setProperty('--icon-opacity', String(ICON_OPACITY[iconSize]));
    // Wave 14k: Secondary-Scale für Controls, die weniger Finger-Präzision
    // brauchen (Style-Switcher oben links). Eine Stufe kleiner als der
    // Haupt-Glove-Mode → medium→small, large→medium. So wächst der Top-Left
    // beim Fahren nicht unnötig in den Map-Viewport rein.
    root.style.setProperty('--icon-scale-secondary', String(ICON_SCALE_SECONDARY[iconSize]));
    root.style.setProperty(
      '--icon-opacity-secondary',
      String(ICON_OPACITY_SECONDARY[iconSize]),
    );
    // Wave 14l: Chrome-Alpha — macht die schwarze Pill-Fläche ab Medium (primary)
    // / Large (secondary) durchsichtig, damit der User Trails darunter erahnen
    // kann. Die Icons selbst bleiben opak (durch separate --icon-opacity-*).
    root.style.setProperty('--chrome-bg-alpha-primary', String(CHROME_BG_ALPHA_PRIMARY[iconSize]));
    root.style.setProperty(
      '--chrome-border-alpha-primary',
      String(CHROME_BORDER_ALPHA_PRIMARY[iconSize]),
    );
    root.style.setProperty(
      '--chrome-bg-alpha-secondary',
      String(CHROME_BG_ALPHA_SECONDARY[iconSize]),
    );
    root.style.setProperty(
      '--chrome-border-alpha-secondary',
      String(CHROME_BORDER_ALPHA_SECONDARY[iconSize]),
    );
    root.style.setProperty('--chrome-blur-primary', CHROME_BLUR_PRIMARY[iconSize]);
    root.style.setProperty('--chrome-blur-secondary', CHROME_BLUR_SECONDARY[iconSize]);
    // Wave 14ca-fix12: Tertiary — Zoom-Buttons + FullscreenToggle skalieren
    // wie Primary, aber Opacity fällt aggressiver damit sie bei Glove-Mode
    // nicht die halbe Karte zudecken (User-Wunsch).
    root.style.setProperty('--icon-scale-tertiary', String(ICON_SCALE_TERTIARY[iconSize]));
    root.style.setProperty('--icon-opacity-tertiary', String(ICON_OPACITY_TERTIARY[iconSize]));
    return () => {
      // Beim Unmount NICHT zurücksetzen — es ist App-Root, unmountet nie im
      // normalen Betrieb, aber falls HMR: saubere Defaults zurücklegen.
      root.style.setProperty('--icon-scale', '1');
      root.style.setProperty('--icon-opacity', '1');
      root.style.setProperty('--icon-scale-secondary', '1');
      root.style.setProperty('--icon-opacity-secondary', '1');
      root.style.setProperty('--chrome-bg-alpha-primary', '0.85');
      root.style.setProperty('--chrome-border-alpha-primary', '0.5');
      root.style.setProperty('--chrome-bg-alpha-secondary', '0.85');
      root.style.setProperty('--chrome-border-alpha-secondary', '0.5');
      root.style.setProperty('--chrome-blur-primary', '8px');
      root.style.setProperty('--chrome-blur-secondary', '8px');
    };
  }, [iconSize]);
  // Admin-Crux-Queue: flushen beim App-Start und jedes Mal wenn wir online gehen.
  // User-Wunsch: gesetzte Cruxes "müssen unbedingt auch offline zugänglich sein"
  // UND zum Server gepusht werden. Die appendCruxPoint-Helper parkt bei Netz-
  // Fehlern einen Eintrag in cruxQueue (IDB); hier holen wir den stumm wieder ab.
  const online = useOnline();
  const [adminToken] = useAdminToken();
  useEffect(() => {
    if (!online || !adminToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await flushCruxQueue(adminToken);
        if (!cancelled && res.synced > 0) {
          console.info(`[app] crux-queue flush: ${res.synced} synced`);
        }
      } catch (err) {
        // Sollte nie passieren (flushCruxQueue wirft nicht) — defensiv.
        console.warn('[app] crux-queue flush crashed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [online, adminToken]);

  // Wave 14ca-fix30 — cross-device-sync.
  //   - Beim App-Start: Setter-Listener installieren (intercepts localStorage
  //     setItem für SYNCED_PREF_KEYS), dann initial pull vom Server.
  //   - Bei jedem online-Event: nochmal pullen falls in der offline-Phase
  //     etwas auf einem anderen Gerät geändert wurde.
  // installPrefSyncListener ist idempotent — sicher mehrmals aufgerufen.
  useEffect(() => {
    installPrefSyncListener();
    // Wave 14cg Part 2: Ride-Sync-Background-Hook. installRideSyncBackground
    // ist idempotent und hängt nur einen 'online'-Listener dran + macht
    // einen initialen Flush für pending Queue-Einträge aus vorheriger Session.
    installRideSyncBackground();
  }, []);
  useEffect(() => {
    if (!online) return;
    pullPrefs().catch(() => {});
    pullCustomTrails().catch(() => {});
    // Wave 14cg Part 2: Server → IDB merge für Rides die auf anderen Geräten
    // angelegt wurden. Silent-fail bei 401 (nicht eingeloggt).
    pullRides().catch(() => {});
  }, [online]);

  // Wave 14bq: Elevation-Queue flushen beim App-Start + jedem online-Event.
  // User-Report: Höhenprofil vom Handy landete nicht beim Backend, also
  // musste das andere Gerät neu rechnen. Jetzt parkt ein fehlgeschlagener
  // Push in elevationQueue (IDB) und wird hier stumm abgeholt. Kein Token
  // nötig — /elevation-samples ist public unauth.
  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await flushElevationQueue();
        if (!cancelled && (res.synced > 0 || res.dropped > 0)) {
          console.info(
            `[app] elevation-queue flush: ${res.synced} synced, ${res.dropped} dropped, ${res.stillPending} pending`,
          );
        }
      } catch (err) {
        console.warn('[app] elevation-queue flush crashed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [online]);

  // Wave 14cf-fix9: Admin-POI-Queue flushen — analog crux/elevation.
  // Wenn Admin offline einen POI markiert, parkt postAdminPoi() die
  // Payload in adminPoiQueue. Hier holen wir die regelmäßig wieder ab.
  // Trigger:
  //   1. App-Start (online == true beim Mount)
  //   2. Übergang offline → online (via useOnline)
  //   3. Periodisch alle 5 min während App offen + online ist —
  //      fängt z.B. flaky-Wifi ab wo der online-Event nicht sauber
  //      flippt, oder den Fall dass der Admin GPS-mark nach App-
  //      Start ohne weiteren online-Event passiert.
  useEffect(() => {
    if (!online) return;
    let cancelled = false;
    let intervalId: number | null = null;
    const runFlush = async () => {
      try {
        const res = await flushAdminPoiQueue();
        if (!cancelled && (res.pushed > 0 || res.failed > 0)) {
          console.info(
            `[app] admin-poi-queue flush: ${res.pushed} pushed, ${res.failed} failed`,
          );
        }
        // Wenn was pushed wurde: triggert auch Map-Refresh damit die
        // optimistische local-id-Markierung mit der echten Server-ID
        // ersetzt wird (postAdminPoi macht das im flushAdminPoiQueue-Pfad
        // aber der Map-Layer reagiert nur auf 'adt:poi-added'-Events).
        if (!cancelled && res.pushed > 0) {
          window.dispatchEvent(
            new CustomEvent('adt:poi-added', { detail: { kind: 'admin' } }),
          );
        }
      } catch (err) {
        if (!cancelled) console.warn('[app] admin-poi-queue flush crashed', err);
      }
    };
    void runFlush();
    intervalId = window.setInterval(() => {
      if (cancelled) return;
      void runFlush();
    }, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      if (intervalId != null) window.clearInterval(intervalId);
    };
  }, [online]);

  // MapPage ist dauerhaft gemountet — siehe Langform-Kommentar unten. Wenn wir
  // sie bei Tab-Wechsel unmounten würden, killt MapLibre's useEffect-Cleanup
  // die komplette Map-Instanz (Glyphs, geparste Vector-Tiles, PMTiles-Header,
  // Render-Worker). Offline ist der Rebuild nicht wiederherstellbar.
  const mapRouteActive = location.pathname === '/';

  // iOS Safari kann bei verdecktem Canvas (Overlay drüber) den GPU-Context
  // "pausieren" oder den Drawing-Buffer freigeben. Beim Zurückkommen auf die
  // Karten-Route feuern wir zwei Signale:
  //   1) 'resize' — triggert map.resize() (günstig, aber kein Forced Repaint)
  //   2) CustomEvent 'adt:map-revealed' — MapView hört darauf und ruft explizit
  //      map.triggerRepaint() auf. Das erzwingt einen neuen Frame selbst wenn
  //      sich die Größe nicht geändert hat (der typische Case wenn das Overlay
  //      weggeklappt wird).
  // Mehrfach-Feuer mit ansteigendem Delay, weil iOS Safari manchmal einen
  // zweiten Paint-Zyklus braucht bis der Canvas tatsächlich Pixel zeigt.
  const prevRouteRef = useRef(location.pathname);
  useEffect(() => {
    if (mapRouteActive && prevRouteRef.current !== '/') {
      const pokes = [50, 200, 500].map((delay) =>
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
          window.dispatchEvent(new CustomEvent('adt:map-revealed'));
        }, delay),
      );
      return () => {
        for (const id of pokes) clearTimeout(id);
      };
    }
    prevRouteRef.current = location.pathname;
    return;
  }, [location.pathname, mapRouteActive]);

  // Wave 14cb: Fullscreen-Mode (User-Wunsch). Wenn aktiv → TabBar verschwindet.
  // BottomBar / Sheets / Höhenprofil-Strip werden im MapPage selbst weggeblendet.
  // Wir hängen NICHT auf einer Route-Bedingung — Fullscreen ist app-weit
  // (nur die Map-Route nutzt es de facto, aber wenn der User auf eine andere
  // Route springt, soll die TabBar wieder sichtbar sein, sonst wäre sie
  // unerreichbar). Daher: TabBar versteckt nur wenn fullscreen UND Map-Route.
  const [fullscreen, setFullscreen] = useState(() => fullscreenBus.get());
  useEffect(() => {
    setFullscreen(fullscreenBus.get());
    return fullscreenBus.subscribe(setFullscreen);
  }, []);
  const hideTabBar = fullscreen && mapRouteActive;

  return (
    <div
      className={`h-full flex flex-col${
        // Wave 14ca-fix64: body-level Klasse fuer Fullscreen, damit CSS-
        // Regeln die MapLibre-Custom-Controls (Locate + Compass im
        // .maplibregl-ctrl-top-right) ausblenden koennen. Diese Controls
        // werden von MapLibre intern positioniert, daher kein React-Gate.
        fullscreen && mapRouteActive ? ' adt-fullscreen' : ''
      }`}
    >
      <main className="flex-1 relative overflow-hidden">
        {/* Wave 14aw: Der prominente Offline-Banner wurde entfernt. Er hat
            das Höhenprofil und Trail-Detail-Sheet oft verdeckt. Offline-
            Status wird jetzt subtil angezeigt:
              1. Style-Switcher oben links bekommt gelben Hintergrund
                 (adt-chrome-secondary → adt-chrome-offline)
              2. BottomBar bekommt eine kleine "offline"-Pill rechts
              3. Satellite-Style wird offline ausgeblendet (bringt eh nix). */}

        {/* MapPage rendert via eigenem `absolute inset-0` fullscreen in main.
            Sie liegt auf der Default-Stacking-Ebene, Overlays legen sich per
            z-10 drüber. Keine extra Wrapper-Div nötig — die würde nur doppelte
            Positionierung erzeugen und u.U. den Canvas-Layout-Pass stören. */}
        <MapPage />

        {/* Wave 14dz: Out-of-region-Banner. Selbst-gated über GPS — taucht
            NUR auf wenn der User einen Position-Fix hat UND der ausserhalb
            ALLER REGIONS.bounds liegt (z. B. Tester in Deutschland). Liegt
            ueber der Map (z-30) aber unter Modals (typisch z-50). Auf
            allen Tabs sichtbar weil hier auf App-Level montiert. */}
        <OutOfRegionBanner />

        {/* Overlay-Routen: alle außer "/" rendern einen eigenen opaken Layer
            der die Map komplett abdeckt. Jede Page hat ihr eigenes
            `absolute inset-0` — wir geben ihnen hier nur z-10 + opaken
            Hintergrund mit einem Wrapper der pointer-events:auto erzwingt,
            damit Taps zuverlässig von der Map-Canvas-Ebene abgefangen werden. */}
        {!mapRouteActive && (
          <div className="absolute inset-0 z-10 bg-turbo-900">
            <Routes>
              {/* /navi ist der Hub-Screen. ULTRA-Fork: nur noch zwei Kacheln
                  (Create custom + My Custom Tours). */}
              <Route path="/navi" element={<NaviPage />} />
              <Route path="/navi/custom" element={<CreateCustomPage />} />
              <Route path="/navi/router" element={<RouterPage />} />
              {/* Corridor-Selfservice: GPX-Upload → Server-Build → Downloads */}
              <Route path="/navi/corridors" element={<CorridorDownloadsPage />} />
              <Route
                path="/navi/custom-trails"
                element={<CustomTrailsPage />}
              />
              <Route
                path="/navi/custom-trails/:id"
                element={<CustomTrailDetailPage />}
              />
              <Route path="/ride" element={<RidePage />} />
              <Route path="/battery" element={<BatteryPage />} />
              <Route path="/offline" element={<OfflinePage />} />
              <Route path="/sos" element={<SOSPage />} />
              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/legal" element={<LegalPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </div>
        )}
      </main>
      {!hideTabBar && <TabBar />}
      <DebugConsole />
      {/* Wave 14ca-fix64: Rain-Mode-Lock-Overlay. Sitzt am Root damit es
          alles abdeckt — Map, TabBar, Sheets. Self-conditional Rendering
          via Bus-Subscription. */}
      <RainModeOverlay />
      {/* Wave 14ca-fix64: Pocket-Mode-Overlay (OLED-Schwarz). Gleiche
          Architektur wie Rain-Mode, andere Anwendung (Energie-Spar statt
          Wasser-Schutz). */}
      <PocketModeOverlay />
      {/* Wave 14dt: globaler Single-Finger-Long-Press (3s) zum Rain-Mode-
          Aktivieren, von jeder Seite aus. Rendert nichts, nur Listener. */}
      <GlobalRainModeGesture />
    </div>
  );
}

function NotFoundPage() {
  const isDev = import.meta.env.DEV;
  return (
    <div className="absolute inset-0 p-6 flex flex-col items-center justify-center text-center">
      <h1 className="text-xl font-semibold mb-2">Page not found</h1>
      <p className="text-white/60 text-sm mb-4">This path doesn't exist in the rider app.</p>
      {isDev && (
        <div className="text-xs text-white/70 bg-turbo-700/30 border border-turbo-500/30 rounded-lg p-3 max-w-xs">
          <div className="font-semibold mb-1">Admin UI?</div>
          <div>Runs in dev mode on its own port:</div>
          <a
            href="http://localhost:5174"
            className="text-turbo-500 underline block mt-1"
            target="_blank"
            rel="noreferrer"
          >
            http://localhost:5174
          </a>
        </div>
      )}
    </div>
  );
}

function TabBar() {
  // Vier sichtbare Tabs: Map, NAVI, SOS, Profile.
  // Ride + Battery bleiben registriert (oben in <Routes>), tauchen aber im
  // TabBar nicht auf.
  //
  // Der ehemalige „Trails"-Tab heisst seit 14bv NAVI und führt zum Hub.
  const location = useLocation();

  // NAVI deckt die /navi-URL-Familie ab — /navi + /navi/*. Die alte
  // „end={to === '/'}"-Logik von NavLink reicht dafür nicht, deshalb ein
  // optionaler `activeWhen`-Predicate pro Tab.
  type Tab = {
    to: string;
    label: string;
    icon: ReactNode;
    badge?: number;
    activeWhen?: (path: string) => boolean;
  };
  const tabs: Array<Tab> = [
    { to: '/',        label: 'Map',     icon: <IconCompass /> },
    {
      to: '/navi',
      label: 'NAVI',
      icon: <IconRoute />,
      // NAVI bleibt aktiv unter allen Routen-Kontexten des Hubs.
      activeWhen: (p) => p === '/navi' || p.startsWith('/navi/'),
    },
    // Wave 14cf: SOS sitzt jetzt in der MITTE — User-Wunsch, weil ein
    // SOS-Tab im Notfall einhändig leicht erreichbar sein soll und die
    // Mitte ergonomisch am dichtesten unterm Daumen ist.
    // Wave 14ce: Offline-Tab durch SOS ersetzt (User-Wunsch — Offline-
    // Sektion wandert in Profile). /offline-Route bleibt registriert,
    // ist aber nur noch über die Profile-Page erreichbar.
    { to: '/sos',     label: 'SOS',     icon: <IconSos /> },
    { to: '/profile', label: 'Profile', icon: <IconUser /> },
  ];

  const isTabActive = (tab: Tab) => {
    if (tab.activeWhen) return tab.activeWhen(location.pathname);
    if (tab.to === '/') return location.pathname === '/';
    return (
      location.pathname === tab.to || location.pathname.startsWith(tab.to + '/')
    );
  };

  // Wave 14cf-fix6: TabBar wieder atmungsaktiv. fix51 hatte das Ding so
  // weit eingedampft (Pill 48×28, w-5 Icon, pt-0/pb-0) dass die Icons
  // gequetscht aussahen + an die safe-area klebten. SMA-Direktvergleich
  // hat klar gezeigt: deren Bar ist deutlich höher, Icons ~24px, Labels
  // luftig.
  // Jetzt: pt-1.5 oben, Pill 56×36 mit h-6/w-6 Icons, gap-1 zwischen Pill
  // und Label. safe-bottom liefert volle safe-area-inset (siehe styles/
  // index.css fix6). Gesamthöhe ~ 76px inkl Home-Indicator-Padding.
  return (
    <nav className="safe-bottom bg-turbo-900 border-t border-turbo-700/40 grid grid-cols-4 pt-1.5 pb-0">
      {tabs.map((t) => {
        const isActive = isTabActive(t);
        return (
          <NavLink
            key={t.to}
            to={t.to}
            className="flex flex-col items-center gap-1 py-0 no-underline"
          >
            <span
              className={`relative flex items-center justify-center w-14 h-9 rounded-xl transition-colors duration-150 ${
                isActive ? 'bg-orange-500 text-white' : 'text-white/55'
              }`}
            >
              {t.icon}
              {t.badge != null && t.badge > 0 && <TabBadge count={t.badge} />}
            </span>
            <span
              className={`text-[10px] leading-none font-semibold tracking-[0.12em] uppercase transition-colors duration-150 ${
                isActive ? 'text-orange-500' : 'text-white/55'
              }`}
            >
              {t.label}
            </span>
          </NavLink>
        );
      })}
    </nav>
  );
}

/**
 * Notification-Style-Badge oben rechts an einem Tab-Icon. Orange Pill, weiße
 * Bold-Ziffer, dünner dunkler Ring damit der Badge vom orange-aktiven Pill
 * visuell abgesetzt bleibt (ohne Ring würde er mit dem Pill verschmelzen).
 *
 * Breite wächst mit der Stellenzahl — dank `min-w`. Bei >999 zeigen wir
 * "999+" (vier Zeichen), die Pille wird knapp 26px breit, passt gerade
 * noch rein, ohne die Tab-Label-Zeile zu verschieben.
 */
function TabBadge({ count }: { count: number }) {
  const label = count > 999 ? '999+' : String(count);
  return (
    <span
      className="absolute -top-1 -right-2 min-w-[18px] h-[18px] px-[5px] rounded-full bg-orange-500 text-white text-[10px] font-bold leading-none flex items-center justify-center tabular-nums ring-2 ring-turbo-900 shadow-sm"
      aria-hidden="true"
    >
      {label}
    </span>
  );
}

// Icons ---------------------------------------------------------------------
// Alle vier Icons sind klassische lucide-style Line-Icons: 24×24 viewBox,
// stroke-width 2, round caps. Gerendert als `w-5 h-5` (20px) — passt gut
// in den 36px-Pill ohne zu zentriert zu wirken. `fill="none"` hält sie
// minimalistisch, `stroke="currentColor"` macht sie färbbar via parent.

function IconCompass() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

function IconRoute() {
  // Lucide-"Route"-Inspiration: zwei Pins oben-rechts + unten-links, dazwischen
  // eine geschwungene Linie, die den Trail repräsentiert. Logischer für Trails
  // als ein Berg (User-Wunsch Batch 14b: "trails am besten so mit der linie,
  // sieht logischer aus als der 'berg'").
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <path d="M8 19h7a4 4 0 0 0 0-8H9a4 4 0 0 1 0-8h7" />
    </svg>
  );
}

/**
 * "DH Run"-Icon — "DH" (Downhill) als Line-Buchstaben, stilistisch analog zum
 * Trails-Icon (IconRoute): Stroke-basierte Formen + ein kleiner Kringel pro
 * Letter als Akzent-Motiv, ganz im Line-Icon-Set der App.
 *
 * Layout in viewBox 24×24:
 *   - Beide Letter y=7..19 (12 Einheiten hoch)
 *   - D links (x=4..10): vertikaler Stave + halbrunde Bowl nach rechts
 *   - H rechts (x=13..20): zwei Vertikalen + Crossbar in der Mitte
 *   - Kringel (r≈1.5) pro Letter oben am Stave (x=4/13, y≈5.5), tangential
 *     an die Letter-Oberkante — exakt das Verhältnis Ring↔Linie wie bei
 *     IconRoute (Ring r=2, Linie startet 2px weg am Endpunkt).
 *
 * Alternativen die ich verworfen habe: Zigzag-Ski-Run (zu generisch, sieht
 * aus wie ein Blitz), Kette-Symbol (zu IT-ig, riecht nach "Blockchain"),
 * Mountain+Pfeil (kollidiert mit dem Trails-Icon-Konzept), der vorherige
 * Chevron-Entwurf (funktionierte, aber die DH-Letter sind expliziter und
 * passen besser zum User-Vokabular "DH Runs").
 */
function IconRuns() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      {/* D als Line-Letter: Stave + halbrunde Bowl (Radius 6 = Halbhöhe des
          Staves, ergibt einen sauberen Halbkreis). */}
      <path d="M 4 7 L 4 19" />
      <path d="M 4 7 A 6 6 0 0 1 4 19" />
      {/* Kringel auf dem D — tangential an der Stave-Oberkante. */}
      <circle cx="4" cy="5.5" r="1.5" />

      {/* H als Line-Letter: zwei Vertikalen + Crossbar in der Mitte. */}
      <path d="M 13 7 L 13 19" />
      <path d="M 20 7 L 20 19" />
      <path d="M 13 13 L 20 13" />
      {/* Kringel auf dem H — analoge Position wie beim D, symmetrisch. */}
      <circle cx="13" cy="5.5" r="1.5" />
    </svg>
  );
}

function IconWifiOff() {
  // Wifi mit Diagonal-Slash — "keine Verbindung, aber Offline-Karten da".
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      <path d="M12 20h.01" />
      <path d="M8.5 16.43a5 5 0 0 1 6.98 0" />
      <path d="M5 12.55a11 11 0 0 1 4.32-2.35" />
      <path d="M14.08 10.2a11 11 0 0 1 4.92 2.35" />
      <path d="M12 12a10 10 0 0 0-1.54.12" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </svg>
  );
}

/**
 * Wave 14ce: SOS-Icon für den TabBar. Klassisches Notfall-Dreieck mit
 * Ausrufezeichen — lucide-style ("triangle-alert"), Stroke 2.
 *
 * Wave 14cf-fix21: Dreieck ROT (#ef4444 = Tailwind red-500) — wie ein
 * Warnblinkdreieck im Auto. Vorher war's neutral-grau (`currentColor` →
 * `text-white/55`-Inactive bzw. `text-white`-Active aus dem TabBar-Pill).
 * Inline `color: #ef4444` schlägt beide Cascade-Pfade, damit das Icon
 * sowohl im inaktiven Zustand als auch im aktiven Orange-Pill rot bleibt.
 * Stroke bleibt via `currentColor` an die Color-Property gebunden.
 */
function IconSos() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="w-6 h-6"
      style={{ color: '#ef4444' }}
      aria-hidden="true"
    >
      <path d="M21.73 18 13.73 4a2 2 0 0 0-3.46 0L2.27 18a2 2 0 0 0 1.73 3h16a2 2 0 0 0 1.73-3Z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}

function IconUser() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden="true">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
